import React, { useEffect, useState } from "react";
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import Loading from "../common/Loading";
import canopyLogo from "../../assets/icons/canopy.png";
import "../../styles/login.css";

export default function Login() {
    const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
    const navigate = useNavigate();
    const [isHovered, setIsHovered] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    // If already authenticated, redirect to dashboard
    useEffect(() => {
        if(isAuthenticated) {
            navigate('/dashboard');
        }
    }, [isAuthenticated, navigate]);

    // Track mouse position for parallax effect
    useEffect(() => {
        const handleMouseMove = (e) => {
            setMousePosition({
                x: (e.clientX / window.innerWidth - 0.5) * 20,
                y: (e.clientY / window.innerHeight - 0.5) * 20
            });
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    if (isLoading) {
        return <Loading />
    }

    return (
        <div className="login-container">
            {/* Animated background gradient */}
            <div className="animated-background">
                <div className="gradient-orb orb-1" style={{
                    transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)`
                }}></div>
                <div className="gradient-orb orb-2" style={{
                    transform: `translate(${mousePosition.x * -0.3}px, ${mousePosition.y * -0.3}px)`
                }}></div>
                <div className="gradient-orb orb-3" style={{
                    transform: `translate(${mousePosition.x * 0.2}px, ${mousePosition.y * 0.2}px)`
                }}></div>
            </div>

            {/* Floating IoT elements */}
            <div className="floating-elements">
                <div className="floating-sensor sensor-1">
                    <div className="sensor-icon">🌡️</div>
                    <div className="sensor-pulse"></div>
                </div>
                <div className="floating-sensor sensor-2">
                    <div className="sensor-icon">💧</div>
                    <div className="sensor-pulse"></div>
                </div>
                <div className="floating-sensor sensor-3">
                    <div className="sensor-icon">☀️</div>
                    <div className="sensor-pulse"></div>
                </div>
                <div className="floating-sensor sensor-4">
                    <div className="sensor-icon">🌱</div>
                    <div className="sensor-pulse"></div>
                </div>
            </div>

            {/* Connection lines */}
            <svg className="connection-lines" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                    <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="transparent" />
                        <stop offset="50%" stopColor="#4CAF50" />
                        <stop offset="100%" stopColor="transparent" />
                    </linearGradient>
                </defs>
                <path d="M20,30 Q50,20 80,30" stroke="url(#connectionGradient)" strokeWidth="1" fill="none" opacity="0.3">
                    <animate attributeName="stroke-dasharray" values="0,100;100,0;0,100" dur="3s" repeatCount="indefinite" />
                </path>
                <path d="M25,70 Q50,80 75,70" stroke="url(#connectionGradient)" strokeWidth="1" fill="none" opacity="0.3">
                    <animate attributeName="stroke-dasharray" values="0,100;100,0;0,100" dur="3s" repeatCount="indefinite" begin="1s" />
                </path>
            </svg>

            {/* Main content */}
            <div className="login-content" style={{
                transform: `translate(${mousePosition.x * 0.1}px, ${mousePosition.y * 0.1}px)`
            }}>
                {/* Logo */}
                <div className="logo-container">
                    <img src={canopyLogo} alt="Canopy Systems" className="main-logo" />
                    <div className="logo-glow"></div>
                </div>

                {/* Welcome text */}
                <div className="welcome-section">
                    <h1 className="welcome-title">
                        <span className="title-line">Welcome to</span>
                        <span className="title-line highlight">Canopy Systems</span>
                    </h1>
                    <p className="welcome-subtitle">
                        Your hub for automating your growing setup.
                    </p>
                </div>

                {/* Feature highlights */}
                <div className="features-grid">
                    <div className="feature-item">
                        <div className="feature-icon">📊</div>
                        <span>Real-time Analytics</span>
                    </div>
                    <div className="feature-item">
                        <div className="feature-icon">🤖</div>
                        <span>Smart Automation</span>
                    </div>
                    <div className="feature-item">
                        <div className="feature-icon">🔒</div>
                        <span>Secure & Reliable</span>
                    </div>
                </div>

                {/* Login button */}
                <div className="login-button-container">
                    <button 
                        className={`login-button ${isHovered ? 'hovered' : ''}`}
                        onClick={() => loginWithRedirect()}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        <span className="button-text">Get Started</span>
                        <span className="button-icon">→</span>
                        <div className="button-ripple"></div>
                    </button>
                </div>

                {/* Footer */}
                <div className="login-footer">
                    <p>Experience the future of plant monitoring</p>
                </div>
            </div>

            {/* Particle effects - independent of mouse movement */}
            <div className="particles">
                {[...Array(20)].map((_, i) => (
                    <div 
                        key={i} 
                        className="particle"
                        style={{
                            animationDelay: `${i * 0.1}s`,
                            left: `${Math.random() * 100}%`,
                            animationDuration: `${3 + Math.random() * 2}s`
                        }}
                    ></div>
                ))}
            </div>
        </div>
    );
}